import mainTheme, { buildTheme, darkTheme, mergeThemes } from './theme';
import pickActiveAccent from './pickActiveAccent';

/**
 * Skin fields AppWrapper places on the app theme beside the mainTheme tokens.
 * Build replaces the app theme with its own provider, so these have to be
 * carried across explicitly or TV mode loses the organisation's primary colour.
 * The skin's darknessMode (`isDarkMode`) is deliberately not carried: the
 * per-user Appearance setting decides Build's mode.
 */
export const SKIN_THEME_FIELDS = [
  'colorAccent',
  'colorPrimary',
  'colorSecondary',
  'isAccentDarkTheme',
  'isPrimaryDarkTheme',
  'isSecondaryDarkTheme',
];

/** Only the defined skin fields, so an absent skin never shadows a key with undefined. */
export const pickSkinFields = (theme = {}) => SKIN_THEME_FIELDS.reduce((acc, key) => (
  theme[key] === undefined ? acc : { ...acc, [key]: theme[key] }
), {});

/**
 * The theme Build renders under. Precedence, lowest first: mainTheme, the
 * skin fields, buildTheme (light high-contrast), darkTheme when dark, then the
 * active-rail accent chosen for this mode.
 */
/**
 * Legacy skin fields that shared components still read -- the _TBComponents
 * Modal wrapper and Title (backgroundColorL2/L3, textColor, noBorderColorForm).
 * mainTheme never defines them and they are not skin fields, so under Build
 * they resolve to undefined and those components fall back to their light
 * literals: in dark mode that is a white modal shell under 80%-white text (the
 * Support modal). Mapped from the dark tokens; light Build is left as-is.
 */
const legacyFieldsFromTokens = ({ colors }) => ({
  textColor: colors.text,
  backgroundColorL2: colors.surfaceElevated,
  backgroundColorL3: colors.surfaceInput,
  noBorderColorForm: colors.borderMedium,
});

const composeBuildTheme = ({ isDark, skinAccent, skinFields = {} }) => {
  const accentLayer = { colors: { iconActive: pickActiveAccent(skinAccent, isDark) } };
  if (!isDark) {
    return mergeThemes(mainTheme, skinFields, buildTheme, accentLayer);
  }
  const legacyFields = legacyFieldsFromTokens(darkTheme);
  return mergeThemes(mainTheme, skinFields, buildTheme, darkTheme, legacyFields, accentLayer);
};

export default composeBuildTheme;
